// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/components/Programs.js

import React, { useState } from 'react';
import { FiBookOpen as BookIcon, FiChevronRight as ChevronRight, FiX, FiClock, FiUsers, FiAward } from 'react-icons/fi';
import useBrandColors from '../utils/useBrandColors';

// Program Detail Modal Component
const ProgramDetailModal = ({ program, onClose }) => {
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!program) return null;

  // Helper function to safely render courses
  const renderCourses = (courses) => {
    if (!courses || !Array.isArray(courses) || courses.length === 0) {
      return <p className="text-sm" style={{ color: '#6B7280' }}>No courses listed</p>;
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {courses.map((course, idx) => {
          // If course is an object, try to extract name or title
          let courseName = course;
          if (typeof course === 'object' && course !== null) {
            courseName = course.name || course.title || course.courseName || `Course ${idx + 1}`;
          }
          return (
            <div
              key={idx}
              className="px-3 py-2 rounded-lg text-xs"
              style={{ 
                backgroundColor: `${primary}05`,
                borderColor: `${primary}15`,
              }}
            >
              {courseName}
            </div>
          );
        })}
      </div>
    );
  };

  // Helper function to safely render features
  const renderFeatures = (features) => {
    if (!features || !Array.isArray(features) || features.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {features.map((feature, idx) => {
          let featureName = feature;
          if (typeof feature === 'object' && feature !== null) {
            featureName = feature.name || feature.title || feature.featureName || `Feature ${idx + 1}`;
          }
          return (
            <span
              key={idx}
              className="px-3 py-1.5 rounded-full text-xs"
              style={{ 
                backgroundColor: `${primary}10`,
                color: primary,
              }}
            >
              {featureName}
            </span>
          );
        })}
      </div>
    );
  };

  // Helper function to safely render career opportunities
  const renderCareerOpportunities = (careers) => {
    if (!careers || !Array.isArray(careers) || careers.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-wrap gap-2">
        {careers.map((career, idx) => {
          let careerName = career;
          if (typeof career === 'object' && career !== null) {
            careerName = career.name || career.title || career.careerName || `Career ${idx + 1}`;
          }
          return (
            <span
              key={idx}
              className="px-3 py-1.5 rounded-full text-xs"
              style={{ 
                backgroundColor: `${secondary}15`,
                color: secondary,
              }}
            >
              {careerName}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative animate-fadeIn"
        style={{ fontFamily: fontBody }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <FiX className="w-5 h-5" style={{ color: '#6B7280' }} />
        </button>

        {/* Program Header */}
        <div className="flex items-start gap-4 mb-6">
          {program.thumbnail ? (
            <img 
              src={program.thumbnail} 
              alt={program.name}
              className="w-16 h-16 rounded-xl object-cover"
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center"
              style={{ 
                background: `linear-gradient(135deg, ${primary}15, ${secondary}15)`,
              }}
            >
              <BookIcon className="w-8 h-8" style={{ color: secondary }} />
            </div>
          )}
          <div>
            <h2 
              className="text-2xl font-bold"
              style={{ 
                color: primary,
                fontFamily: fontHeading,
              }}
            >
              {program.name}
            </h2>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-sm" style={{ color: '#6B7280' }}>
              {program.duration && (
                <span className="flex items-center gap-1">
                  <FiClock className="w-4 h-4" style={{ color: secondary }} />
                  {program.duration}
                </span>
              )}
              {program.type && (
                <span className="flex items-center gap-1">
                  <FiUsers className="w-4 h-4" style={{ color: secondary }} />
                  {program.type}
                </span>
              )}
              {program.level && (
                <span className="flex items-center gap-1">
                  <FiAward className="w-4 h-4" style={{ color: secondary }} />
                  {program.level}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {program.description && (
          <div className="mb-6">
            <h3 
              className="text-sm font-semibold mb-2"
              style={{ color: primary }}
            >
              About this Program
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>
              {program.description}
            </p>
          </div>
        )}

        {/* Features */}
        {program.features && program.features.length > 0 && (
          <div className="mb-6">
            <h3 
              className="text-sm font-semibold mb-2"
              style={{ color: primary }}
            >
              Key Features
            </h3>
            {renderFeatures(program.features)}
          </div>
        )}

        {/* Courses */}
        {program.courses && program.courses.length > 0 && (
          <div className="mb-6">
            <h3 
              className="text-sm font-semibold mb-2"
              style={{ color: primary }}
            >
              Courses ({program.courses.length})
            </h3>
            {renderCourses(program.courses)}
          </div>
        )}

        {/* Career Opportunities */}
        {program.careerOpportunities && program.careerOpportunities.length > 0 && (
          <div className="mb-6">
            <h3 
              className="text-sm font-semibold mb-2"
              style={{ color: primary }}
            >
              Career Opportunities
            </h3>
            {renderCareerOpportunities(program.careerOpportunities)}
          </div>
        )}

        {/* Eligibility */}
        {program.eligibility && (
          <div className="mb-6">
            <h3 
              className="text-sm font-semibold mb-2"
              style={{ color: primary }}
            >
              Eligibility
            </h3>
            <p className="text-sm" style={{ color: '#6B7280' }}>
              {program.eligibility}
            </p>
          </div>
        )}

        {/* Apply Button */}
        <button
          className="w-full py-3 rounded-xl font-medium transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
          style={{ 
            backgroundColor: primary,
            color: '#ffffff',
            fontFamily: fontBody,
          }}
        >
          Apply Now
        </button>
      </div>
    </div>
  );
};

// Main Programs Component
const Programs = ({ 
  programs = [], 
  onViewProgram = () => {}, 
  apiBaseURL = '',
  sectionTitle,
  sectionSubtitle,
  displayStyle 
}) => {
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  console.log('🔍 Programs - Items:', programs.length, programs);

  if (!Array.isArray(programs) || programs.length === 0) {
    return null;
  }

  const title = sectionTitle || 'Our Programs';
  const subtitle = sectionSubtitle || 'Carefully designed programs to help students achieve academic and career goals';

  const handleViewProgram = (program) => {
    setSelectedProgram(program);
    setIsModalOpen(true);
    if (onViewProgram) {
      onViewProgram(program);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProgram(null);
  };

  // Helper function to safely get course count
  const getCourseCount = (courses) => {
    if (!courses || !Array.isArray(courses)) return 0;
    return courses.length;
  };

  // Helper function to safely render features in card
  const renderCardFeatures = (features) => {
    if (!features || !Array.isArray(features) || features.length === 0) {
      return null;
    }

    return (
      <div className="mt-3 flex flex-wrap gap-1.5">
        {features.slice(0, 2).map((feature, idx) => {
          let featureName = feature;
          if (typeof feature === 'object' && feature !== null) {
            featureName = feature.name || feature.title || feature.featureName || `Feature ${idx + 1}`;
          }
          return (
            <span 
              key={idx} 
              className="text-xs px-2 py-1 rounded-full"
              style={{ 
                backgroundColor: `${primary}10`,
                color: primary,
              }}
            >
              {featureName}
            </span>
          );
        })}
        {features.length > 2 && (
          <span 
            className="text-xs px-2 py-1 rounded-full"
            style={{ 
              backgroundColor: `${primary}05`,
              color: '#9CA3AF',
            }}
          >
            +{features.length - 2}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <section id="programs" className="py-20 md:py-28 bg-gray-50/50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                Academic Programs
              </span>
              <h2 
                className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                style={{ 
                  color: primary,
                  fontFamily: fontHeading,
                }}
              >
                {title}
              </h2>
              <p 
                className="text-lg font-light max-w-2xl mx-auto"
                style={{ 
                  color: '#6B7280',
                  fontFamily: fontBody,
                }}
              >
                {subtitle}
              </p>
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>

            {/* Programs Grid */}
            <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {programs.map((prog) => (
                <div
                  key={String(prog._id || prog.name)}
                  className="group bg-white rounded-2xl p-6 border transition-all duration-500 hover:shadow-xl hover:-translate-y-1"
                  style={{ borderColor: `${primary}15` }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      {prog.thumbnail ? (
                        <img 
                          src={prog.thumbnail} 
                          alt={prog.name}
                          className="w-12 h-12 rounded-xl object-cover"
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center transition-colors"
                          style={{ 
                            backgroundColor: `${primary}10`,
                            color: secondary,
                          }}
                        >
                          <BookIcon className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="text-lg font-bold mb-0.5"
                        style={{ 
                          color: '#1F2937',
                          fontFamily: fontHeading,
                        }}
                      >
                        {prog.name}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2 text-sm" style={{ color: '#6B7280' }}>
                        {prog.duration && (
                          <span>{prog.duration}</span>
                        )}
                        {prog.duration && prog.type && (
                          <span className="text-gray-300">•</span>
                        )}
                        {prog.type && (
                          <span>{prog.type}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {prog.description && (
                    <p 
                      className="text-sm mt-3 leading-relaxed line-clamp-3"
                      style={{ color: '#6B7280' }}
                    >
                      {prog.description}
                    </p>
                  )}

                  {renderCardFeatures(prog.features)}

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs font-light" style={{ color: '#9CA3AF' }}>
                      {getCourseCount(prog.courses)} courses
                    </span>
                    <button
                      type="button"
                      onClick={() => handleViewProgram(prog)}
                      className="group/btn inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:shadow-lg"
                      style={{ 
                        backgroundColor: primary,
                        color: '#ffffff',
                      }}
                    >
                      <span>View Courses</span>
                      <ChevronRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover/btn:translate-x-0.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Program Detail Modal */}
      {isModalOpen && selectedProgram && (
        <ProgramDetailModal 
          program={selectedProgram} 
          onClose={handleCloseModal} 
        />
      )}
    </>
  );
};

export default Programs;